import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, FormControl, InputLabel,
  Select, MenuItem, Chip, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate } from 'react-router-dom';
import { listarGCs, deletarGC } from '../../api/gruposCrescimento';
import ModuleHeader from '../../components/ModuleHeader';

const DIAS_SEMANA = {
  domingo: 'Domingo', segunda: 'Segunda', terca: 'Terça',
  quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sábado'
};

function ConfirmDialog({ open, titulo, mensagem, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent><Typography>{mensagem}</Typography></DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button onClick={onConfirm} color="error" variant="contained">Confirmar</Button>
      </DialogActions>
    </Dialog>
  );
}

function GCLista() {
  const navigate = useNavigate();
  const [gcs, setGcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroPendente, setFiltroPendente] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const params = {};
      if (filtroStatus) params.status = filtroStatus;
      if (filtroPendente) params.aprovado = false;
      const data = await listarGCs(params);
      setGcs(data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar GCs');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, [filtroStatus, filtroPendente]);

  const handleDeletar = async () => {
    if (!confirmDel) return;
    try {
      await deletarGC(confirmDel.id);
      setConfirmDel(null);
      carregar();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao deletar GC');
      setConfirmDel(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <ModuleHeader module="Grupos de Crescimento" title="Grupos de Crescimento" />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Button variant="outlined" startIcon={<DashboardIcon />} onClick={() => navigate('/grupos-crescimento/dashboard')}>
          Dashboard
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/grupos-crescimento/novo')}>
          Novo GC
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filtroStatus} label="Status" onChange={e => setFiltroStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="ativo"><Chip label="Ativo" color="success" size="small" /></MenuItem>
            <MenuItem value="inativo"><Chip label="Inativo" color="default" size="small" /></MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox checked={filtroPendente} onChange={e => setFiltroPendente(e.target.checked)} />}
          label="Pendentes de aprovação"
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : erro ? (
        <Alert severity="error">{erro}</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Líder</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Dia</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Horário</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Aprovado</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Membros</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {gcs.map((gc) => (
                <TableRow
                  key={gc.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/grupos-crescimento/${gc.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{gc.nome}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{gc.lider_nome || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{DIAS_SEMANA[gc.dia_semana] || gc.dia_semana || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{gc.horario || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={gc.status || 'ativo'} color={gc.status === 'inativo' ? 'default' : 'success'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={gc.aprovado ? 'Sim' : 'Não'} color={gc.aprovado ? 'success' : 'warning'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{gc.total_membros ?? '—'}</Typography>
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => navigate(`/grupos-crescimento/${gc.id}/editar`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" color="error" onClick={() => setConfirmDel(gc)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {gcs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Alert severity="info" sx={{ my: 1 }}>Nenhum GC encontrado.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        titulo="Deletar GC"
        mensagem={`Deletar "${confirmDel?.nome}"? Todos os dados vinculados serão removidos.`}
        onConfirm={handleDeletar}
        onCancel={() => setConfirmDel(null)}
      />
    </Container>
  );
}

export default GCLista;
